<template>
  <div>
    <template v-if="savedChecklistsPending">
      <v-skeleton-loader type="table-thead, table-tbody"/>
    </template>
    <template v-else>
      <v-row v-if="savedChecklists.length" class="mb-2">
        <v-col>
          <SearchTextField v-model="search" class="mb-6 checklist-search-field" :tooltipText="$t(`${TRANSLATION_KEY_PREFIX}.search`)" />

          <v-row>
            <v-col md="7" sm="12">
              <v-data-table :headers="headers"
                            :items="tableItems"
                            :items-per-page="-1"
                            :mobile="false"
                            class="saved-checklist-table"
                            style="border: 1px solid rgb(var(--v-theme-border-primary));"
              >
                <template #body="{items, headers}">
                  <SavedChecklistTableBody :columns-count="headers.length"
                                           :items="items"
                  />
                </template>
                <template #bottom></template>
              </v-data-table>
            </v-col>
            <v-col md="5" sm="12">
              <SavedChecklistPreview/>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <div v-else class="d-flex flex-column justify-center align-center py-6">
        <template v-if="!savedChecklistsError">
          <app-material-icon name="checklist" size="xl" color="inherit" />
          <div class="mt-8">
            {{ $t(`${TRANSLATION_KEY_PREFIX}.noData`) }}
          </div>
        </template>
        <template v-else>
          {{ $t(`${TRANSLATION_KEY_PREFIX}.getSavedChecklistsError`) }}
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import { cloneDeep, debounce } from 'lodash-es'
import { mapState } from 'vuex'

import SearchTextField from '@/common/SearchTextField.vue'
import { ISOToShortenedDate } from '@/common/utils/dates'
import SavedChecklistPreview from '@/project/checklist/dialogs/SavedChecklistPreview.vue'
import SavedChecklistTableBody from '@/project/checklist/dialogs/SavedChecklistTableBody.vue'
import { SET_SELECTED_SAVED_CHECKLIST } from '@/store/modules/checklist/mutation_types'

const TRANSLATION_KEY_PREFIX = 'project.checklist.dialogs.SaveChecklistFromTemplateDialog'

export default {
  name: 'SavedChecklistTableTemplate',
  components: {
    SearchTextField,
    SavedChecklistPreview,
    SavedChecklistTableBody,
  },
  data () {
    return {
      searchQuery: '',
      TRANSLATION_KEY_PREFIX,
    }
  },
  computed: {
    ...mapState('checklist', ['savedChecklists', 'selectedSavedChecklist', 'savedChecklistsPending', 'savedChecklistsError']),
    search: {
      get () {
        return this.searchQuery
      },
      set: debounce(function (val) {
        this.searchQuery = val
      }, 1000),
    },
    headers () {
      return [
        {
          key: 'name',
          title: this.$t(`${TRANSLATION_KEY_PREFIX}.savedChecklists.name`),
          class: ['caption', 'font-weight-semi-bold'],
        },
        {
          key: 'share',
          sortable: false,
          title: this.$t(`${TRANSLATION_KEY_PREFIX}.savedChecklists.share`),
          class: ['caption', 'font-weight-semi-bold'],
        },
        {
          key: 'author',
          sortable: false,
          title: this.$t(`${TRANSLATION_KEY_PREFIX}.savedChecklists.author`),
          class: ['caption', 'font-weight-semi-bold'],
        },
        {
          key: 'createdAt',
          title: this.$t(`${TRANSLATION_KEY_PREFIX}.savedChecklists.createdOn`),
          class: ['caption', 'font-weight-semi-bold'],
          width: 150,
        },
        {
          sortable: false,
        },
      ]
    },
    tableItems () {
      return this.searchQuery ? this.filteredSearchResult : this.savedChecklists
    },
    filteredSearchResult () {
      if (!this.searchQuery) return []
      let filteredSavedChecklists = cloneDeep(this.savedChecklists)
      const query = this.searchQuery.toLowerCase()
      let isSavedChecklistIncluded = false

      filteredSavedChecklists = filteredSavedChecklists.filter(savedChecklist => {
        const author = savedChecklist.user
          ? `${savedChecklist.user.firstName} ${savedChecklist.user.lastName}`.toLowerCase()
          : ''
        const matchesSearch = savedChecklist.name.toLowerCase().includes(query) ||
               ISOToShortenedDate(savedChecklist.createdAt).toLowerCase().includes(query) ||
               author.includes(query)

        if (matchesSearch && savedChecklist?.name === this.selectedSavedChecklist?.name) {
          isSavedChecklistIncluded = true
        }

        return matchesSearch
      })

      if (!isSavedChecklistIncluded) {
        this.$store.commit(`checklist/${SET_SELECTED_SAVED_CHECKLIST}`, null)
      }

      return filteredSavedChecklists
    },
  },
}
</script>

<style scoped lang="scss">
  :deep(.saved-checklist-table) {
    th, td {
      border-bottom-color: rgb(var(--v-theme-border-primary)) !important;
    }
    thead {
      background: rgb(var(--v-theme-surface-secondary));
    }
  }
  :deep(.checklist-search-field) {
    width: fit-content !important;
  }
</style>
